import { useState, useEffect } from "react";
import { useWrapRequest } from "utils";
import Calendar from "react-calendar";
import { getCancel } from "services/get-getCancel";
import { useNavigate } from "react-router-dom";
import "components/Cancel/cancel.scss";
import { useRecoilState } from "recoil";
import { cartState, toastState } from "atoms";
import { getNextBillCycleDate } from "utils";
import SecurityCancelForm from "components/Cancel/SecurityCancelForm";
import BillingAddressChangeModal from "components/Checkout/BillingAddressChangeModal";
import { CheckoutFormModel } from "atoms/models";
import { cancelPage } from "services/post-cancelPage";
import { formatCancelDate } from "utils";
import { postRemoveFromCart } from "services/post-removeFromCart";

type ValuePiece = Date | null;

type Value = ValuePiece | [ValuePiece, ValuePiece];

interface CancelData {
  cancellationDate: Date | null;
  cancellationReason: string;
  cancellationComments: string;
  securityFormAcknowledged: boolean;
  securityData: boolean;
}

const Cancel = () => {
  const [calendarDateSelected, setCalendarDateSelected] = useState<Value>();
  const [cancelReasons, setCancelReasons] = useState<string[]>([]);
  const [cancelSecurityForm, setCancelSecurityForm] = useState(false);
  const [earliestCancelDate, setEarliestCancelDate] = useState("");
  const [cart, setCart] = useRecoilState(cartState);
  const [toast, setToast] = useRecoilState(toastState);
  const [cancelData, setCancelData] = useState<CancelData>({
    cancellationDate: null,
    cancellationReason: "",
    cancellationComments: "",
    securityFormAcknowledged: false,
    securityData: false,
  });
  const [cancelSurveyRequestBillingAddress, setCancelSurveyRequestBillingAddress] = useState<CheckoutFormModel>({
    customerNameSuffix: "",
    customerNameFirst: "",
    customerNameLast: "",
    dateOfBirth: "",
    emailAddress: "",
    phoneNumber: "",
    customerSSN: "",
    creditCheckBypass: null,
    useMaxSecurityDeposit: true,
    otherAuthNamePrefix: "",
    otherAuthNameFirst: "",
    otherAuthNameLast: "",
    otherAuthType: "",
    otherAuthSSNLast4: "",
    billingStreetNumber: "0",
    billingStreetName: "",
    billingPOBox: "",
    billingApartmentNumber: "",
    billingCity: "",
    billingState: "",
    billingZipCode: "",
  });
  const wrapRequest = useWrapRequest();
  const navigate = useNavigate();
  const nextBillingDate = Object.keys(cart).length > 0 ? getNextBillCycleDate(cart?.billCycle) : "your next billing date";

  // Create a Date object for "today" and zero out the time to midnight
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Split the earliestCancelDate string (e.g., "2025-06-19") into [year, month, day]
  let date_parts = earliestCancelDate.split("-");
  let year = date_parts[0];
  let month = date_parts[1];
  let day = date_parts[2];

  // Create a Date object for the minimum selectable date (convert string parts to a Date)
  const minDate = earliestCancelDate
    ? new Date(Number(year), Number(month) - 1, Number(day)) // month is 0-based
    : null;

  // Clone minDate to compute the maxDate (2 months later)
  const maxDate = minDate ? new Date(minDate) : null;
  if (maxDate) maxDate.setMonth(maxDate.getMonth() + 2); // Add 2 months
  if (maxDate) maxDate.setHours(0, 0, 0, 0); // Normalize time to midnight

  // Disable all dates before minDate or after maxDate
  const disableOutsideWindow = ({ date }: { date: Date }) => {
    if (!minDate || !maxDate) return true; // disable all if data not ready
    return date < minDate || date > maxDate;
  };

  const disableSubmitButton =
    !cancelData.cancellationDate ||
    !cancelData.cancellationReason ||
    (cancelData.cancellationReason === "Other" && !cancelData.cancellationComments) ||
    (cancelSecurityForm && !cancelData.securityFormAcknowledged);

  const handleConfirm = async () => {
    const modifiedBillingAddress = {
      ...cancelSurveyRequestBillingAddress,
      billingStreetNumber:
        !cancelSurveyRequestBillingAddress.billingStreetNumber || cancelSurveyRequestBillingAddress.billingStreetNumber === " "
          ? "0"
          : cancelSurveyRequestBillingAddress.billingStreetNumber,
      billingPOBox: !cancelSurveyRequestBillingAddress.billingPOBox ? "" : cancelSurveyRequestBillingAddress.billingPOBox,
    };

    const validBillingAddress =
      modifiedBillingAddress.billingStreetNumber &&
      modifiedBillingAddress.billingStreetName &&
      modifiedBillingAddress.billingCity &&
      modifiedBillingAddress.billingState &&
      modifiedBillingAddress.billingZipCode
        ? modifiedBillingAddress
        : null;

    const modifiedCancelData = {
      cancellationReason: cancelData.cancellationReason,
      cancellationComments: cancelData.cancellationComments,
      cancellationDate: formatCancelDate(cancelData.cancellationDate),
      billingAddress: validBillingAddress,
      securityData: cancelData.securityFormAcknowledged ? { securityFormAcknowledged: cancelData.securityFormAcknowledged } : null,
    };

    wrapRequest(async () => {
      const response = await cancelPage(modifiedCancelData);
      const _links = response["_links"];
      if (_links["ec:windowLocation"]?.href) {
        navigate(_links["ec:windowLocation"].href);
      }
    });
  };

  const handleToastErrorMessage = () => {
    if (disableSubmitButton) {
      const issues = [];
      if (!cancelData.cancellationDate) issues.push("select a cancellation date");
      if (!cancelData.cancellationReason) issues.push("choose a reason for cancelling");
      if (cancelSecurityForm && !cancelData.securityFormAcknowledged) issues.push("check the box on the security acknowledgment form");
      if (cancelData.cancellationReason === "Other" && !cancelData.cancellationComments) issues.push("give a reason for cancellation.");

      const message = issues.length ? `To proceed, please ${issues.join(" and, ")}.` : "";
      setToast({
        ...toast,
        show: true,
        variant: "danger",
        header: "Error",
        message: message,
      });
    }
  };

  useEffect(() => {
    window.scroll(0, 0);
    const fetchCancelPage = async () => {
      wrapRequest(async () => {
        const response = await getCancel();
        const _embedded = response["_embedded"];
        const _links = response["_links"];
        if (_links["ec:windowLocation"]?.href) {
          navigate(_links["ec:windowLocation"].href);
        } else {
          if (_embedded["ec:cart"]) {
            setCart(_embedded["ec:cart"]);
          }

          if (_embedded["ec:cancel-reason-list"]) {
            const addOther = [..._embedded["ec:cancel-reason-list"], "Other"];
            setCancelReasons(addOther);
          }

          if (_embedded["ec:cancel-security-form"]) {
            setCancelSecurityForm(_embedded["ec:cancel-security-form"]);
          }

          if (_embedded["ec:earliest-cancel-date"]) {
            setEarliestCancelDate(_embedded["ec:earliest-cancel-date"]);
          }
        }
      });
    };
    fetchCancelPage();
    // eslint-disable-next-line
  }, [navigate]);

  const handleBackButton = () => {
    wrapRequest(async () => {
      // Passing in 0 ID for product since nothing is being added
      // Passing false because not a bundle
      // Passing true because the user is cancelling
      const response = await postRemoveFromCart(0, false, true);
      const _links = response["_links"];
      if (_links["ec:windowLocation"].href) {
        navigate(_links["ec:windowLocation"].href);
      }
    });
  };

  return (
    <div className="cancel-container">
      <h1 className="section-title">Cancel Services</h1>
      <div className="fw-bold m-3">
        <div>
          Thank you for being our valued customer. Please provide more information below on your reason for cancellation. We truly appreciate your
          feedback.
        </div>
        <div className="fw-bold text-danger mt-1">
          To avoid being charged for the next billing cycle, please cancel your service before {nextBillingDate}.
        </div>
      </div>
      <div className="cancel-billing-address-modal">
        <BillingAddressChangeModal
          setCheckoutForm={setCancelSurveyRequestBillingAddress}
          checkoutForm={cancelSurveyRequestBillingAddress}
          buttonTitle="I need to change my billing address"
        />
      </div>
      <div className="mt-5 calendar-form-container">
        <div className="calendar text-center">
          <h4 className="fw-bold mb-1">Select Your Cancellation Date</h4>
          <Calendar
            onChange={(e: any) => {
              setCancelData({ ...cancelData, cancellationDate: e });
              setCalendarDateSelected(e);
            }}
            value={calendarDateSelected}
            tileDisabled={disableOutsideWindow}
            minDate={minDate ?? undefined}
            maxDate={maxDate ?? undefined}
          />
          <div className="fw-bold mt-2">Your service will remain active until the selected date.</div>
        </div>
        {cancelReasons?.length > 0 ? (
          <div className="cancel-survey">
            <div>
              <h4 className="fw-bold mb-1">Why are you cancelling?</h4>
              {cancelReasons.map((reason: string, index: number) => {
                return (
                  <div className="form-check">
                    <input
                      className="form-check-input reasons-radio-buttons"
                      type="radio"
                      name="radioDefault"
                      id={reason}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setCancelData({ ...cancelData, cancellationReason: e.target.id, cancellationComments: "" })
                      }
                    />
                    <label className="form-check-label" htmlFor={`radioDefault${index}`}>
                      {reason}
                    </label>
                  </div>
                );
              })}
              {cancelData.cancellationReason === "Other" ? (
                <div className="form-floating mb-3">
                  <input
                    type="text"
                    className="form-control"
                    id="otherReason"
                    placeholder="Please explain"
                    value={cancelData.cancellationComments}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCancelData({ ...cancelData, cancellationComments: e.target.value })}
                  />
                  <label htmlFor="otherReason">Please explain</label>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
      {cancelSecurityForm ? <SecurityCancelForm cart={cart} cancelData={cancelData} setCancelData={setCancelData} /> : null}
      <div className="cancel-button-container mt-5">
        <button className="btn btn-lg btn-primary me-3" onClick={() => handleBackButton()}>
          Go Back
        </button>
        <div onClick={() => handleToastErrorMessage()}>
          <button
            className="btn btn-lg btn-primary"
            onClick={() => {
              handleConfirm();
            }}
            disabled={disableSubmitButton}
          >
            Confirm Cancellation
          </button>
        </div>
      </div>
    </div>
  );
};

export default Cancel;
